import {
  Box3,
  DataTexture,
  FloatType,
  MathUtils,
  NearestFilter,
  RGBAFormat,
  Uniform,
  Vector3,
} from "three";
import { octree, type OctreeNode } from "d3-octree";
import {
  belongsToPipePatch,
  type PipeMaskPatch,
  type PipeMaskSnapshot,
} from "./pipe-selection-mask";

const MAX_TEXELS = 1_048_576;
const FLOAT_MANTISSA_BITS = 23;
const FLOAT_EPSILON = 2 ** -FLOAT_MANTISSA_BITS;
type SupportNode = { bounds: Box3; next: number; patch: number };

const shader = `
#ifndef SURFACE_FILTER_SKIP_PIPE
uniform vec3 uPipeOrigin;
uniform sampler2D uPipeNodes;
uniform sampler2D uPipePatches;
uniform int uPipeNodeCount;
uniform float uPipeNodeWidth;
uniform float uPipePatchWidth;
vec4 cellEntry(sampler2D source, float index, float width) {
  return texture2D(source, (vec2(mod(index, width), floor(index / width)) + 0.5) / width);
}
#endif
bool selectedSurfacePoint(vec4 world) {
#ifndef SURFACE_FILTER_SKIP_PIPE
  vec3 point = world.xyz / world.w - uPipeOrigin;
  float node = 0.0;
  for (int visited = 0; visited < uPipeNodeCount; visited++) {
    if (node >= float(uPipeNodeCount)) return false;
    vec4 lower = cellEntry(uPipeNodes, node * 2.0, uPipeNodeWidth);
    vec4 upper = cellEntry(uPipeNodes, node * 2.0 + 1.0, uPipeNodeWidth);
    if (any(lessThan(point, lower.xyz)) || any(greaterThan(point, upper.xyz))) {
      node = lower.w;
      continue;
    }
    if (upper.w > 0.5) {
      float index = upper.w - 1.0;
      vec4 center = cellEntry(uPipePatches, index * 2.0, uPipePatchWidth);
      vec4 normal = cellEntry(uPipePatches, index * 2.0 + 1.0, uPipePatchWidth);
      vec3 offset = point - center.xyz;
      float depth = dot(offset, normal.xyz);
      if (abs(depth) <= normal.w && dot(offset, offset) - depth * depth <= center.w * center.w) return true;
    }
    node += 1.0;
  }
#endif
  return false;
}`;

function texture(data: Float32Array, maximumTexels: number): DataTexture {
  const width = MathUtils.ceilPowerOfTwo(
    Math.ceil(Math.sqrt(Math.max(1, data.length / 4))),
  );
  if (width * width > maximumTexels) {
    throw new Error("Pipe mask texture exceeds its resident budget");
  }
  const padded = new Float32Array(width * width * 4);
  padded.set(data);
  const result = new DataTexture(padded, width, width, RGBAFormat, FloatType);
  result.minFilter = NearestFilter;
  result.magFilter = NearestFilter;
  result.needsUpdate = true;
  return result;
}

function patchBounds(patch: PipeMaskPatch): Box3 {
  const halfSize = new Vector3().fromArray(
    patch.normal
      .toArray()
      .map(
        (component) =>
          patch.radius * Math.sqrt(Math.max(0, 1 - component ** 2)) +
          patch.thickness * Math.abs(component),
      ),
  );
  halfSize.addScalar(
    Math.max(1, ...patch.center.toArray().map(Math.abs)) * FLOAT_EPSILON * 2,
  );
  return new Box3(
    patch.center.clone().sub(halfSize),
    patch.center.clone().add(halfSize),
  );
}

/** Exact observed-support hierarchy shared by highlights and opacity filtering. */
export class PipeSelectionGpu {
  readonly shader = shader;
  readonly vertexShader = shader;
  readonly uniforms = {
    uPipeOrigin: new Uniform(new Vector3()),
    uPipeNodes: new Uniform(new DataTexture()),
    uPipePatches: new Uniform(new DataTexture()),
    uPipeNodeCount: new Uniform(0),
    uPipeNodeWidth: new Uniform(1),
    uPipePatchWidth: new Uniform(1),
  };
  private patches: PipeMaskPatch[] = [];
  private nodes: SupportNode[] = [];

  /** @param maximumTexels Optional lower texture budget; the built-in maximum cannot be exceeded. */
  constructor(private readonly maximumTexels = MAX_TEXELS) {}

  /**
   * @param masks Immutable mask revisions, unioned without changing their own origins.
   * @throws When the complete mask cannot fit the resident lookup budget.
   */
  setMasks(masks: readonly PipeMaskSnapshot[]): void {
    const budget = Math.min(MAX_TEXELS, this.maximumTexels);
    const count = masks.reduce((total, mask) => total + mask.patches.length, 0);
    if (count * 2 > budget) {
      throw new Error("Pipe mask exceeds its resident patch budget");
    }
    const origin = masks[0]?.origin.clone() ?? new Vector3();
    const patches = masks.flatMap((mask) =>
      mask.patches.map((patch) => ({
        ...patch,
        center: patch.center.clone().add(mask.origin.clone().sub(origin)),
        normal: patch.normal.clone(),
      })),
    );
    const pointData = new Float32Array(patches.length * 8);
    const indices = new Map<PipeMaskPatch, number>();
    patches.forEach((patch, index) => {
      if (
        ![
          ...patch.center.toArray(),
          ...patch.normal.toArray(),
          patch.radius,
          patch.thickness,
        ].every(Number.isFinite) ||
        patch.radius <= 0 ||
        patch.thickness < 0
      ) {
        throw new Error("Invalid pipe support geometry");
      }
      indices.set(patch, index);
      pointData.set(
        [
          ...patch.center.toArray(),
          patch.radius,
          ...patch.normal.toArray(),
          patch.thickness,
        ],
        index * 8,
      );
    });
    const tree = octree(
      patches,
      (patch) => patch.center.x,
      (patch) => patch.center.y,
      (patch) => patch.center.z,
    );
    const nodes: SupportNode[] = [];
    function allocate(): SupportNode {
      if ((nodes.length + 1) * 2 > budget) {
        throw new Error("Pipe mask exceeds its resident hierarchy budget");
      }
      const record = { bounds: new Box3(), next: 0, patch: -1 };
      nodes.push(record);
      return record;
    }
    function append(node: OctreeNode<PipeMaskPatch>): Box3 {
      const record = allocate();
      if (Array.isArray(node)) {
        node.forEach((child) => record.bounds.union(append(child)));
      } else {
        let leaf: OctreeNode<PipeMaskPatch> | undefined = node;
        while (leaf && !Array.isArray(leaf)) {
          const index = indices.get(leaf.data);
          if (index === undefined) {
            throw new Error("Missing pipe support index");
          }
          const entry = leaf === node && !leaf.next ? record : allocate();
          entry.bounds.copy(patchBounds(leaf.data));
          entry.patch = index;
          entry.next = nodes.length;
          if (entry !== record) record.bounds.union(entry.bounds);
          leaf = leaf.next;
        }
      }
      record.next = nodes.length;
      return record.bounds;
    }
    tree.visit((node) => {
      append(node);
      return true;
    });
    const nodeData = new Float32Array(nodes.length * 8);
    nodes.forEach((node, index) =>
      nodeData.set(
        [
          ...node.bounds.min.toArray(),
          node.next,
          ...node.bounds.max.toArray(),
          node.patch + 1,
        ],
        index * 8,
      ),
    );
    const allocated: DataTexture[] = [];
    try {
      for (const data of [nodeData, pointData]) {
        allocated.push(texture(data, budget));
      }
    } catch (error) {
      allocated.forEach((value) => value.dispose());
      throw error;
    }
    this.dispose();
    [this.uniforms.uPipeNodes.value, this.uniforms.uPipePatches.value] =
      allocated;
    this.uniforms.uPipeNodeWidth.value = allocated[0].image.width;
    this.uniforms.uPipePatchWidth.value = allocated[1].image.width;
    this.uniforms.uPipeNodeCount.value = nodes.length;
    this.uniforms.uPipeOrigin.value.copy(origin);
    this.patches = patches;
    this.nodes = nodes;
  }

  /**
   * @param world World point.
   * @returns The same observed-support membership used by the GPU.
   */
  contains(world: Vector3): boolean {
    const point = world.clone().sub(this.uniforms.uPipeOrigin.value);
    for (let index = 0; index < this.nodes.length;) {
      const node = this.nodes[index];
      if (!node.bounds.containsPoint(point)) {
        index = node.next;
        continue;
      }
      if (
        node.patch >= 0 &&
        belongsToPipePatch(this.patches[node.patch], point)
      ) {
        return true;
      }
      index++;
    }
    return false;
  }

  /** Releases only owned lookup textures. */
  dispose(): void {
    this.uniforms.uPipeNodes.value.dispose();
    this.uniforms.uPipePatches.value.dispose();
    this.uniforms.uPipeNodeCount.value = 0;
    this.patches = [];
    this.nodes = [];
  }
}
